import { X } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { formatBEDateToMediumDateString } from "utils";
import { cn } from "utils/cn";
import {
  formatMoney,
  formatSignedMoney,
  formatSignedPercent,
} from "../../BudgetScenarioCompareModal.utils";
import { ComparisonTableProps } from "./ComparisonTable.types";

const GRID = "grid grid-cols-[1fr_auto_auto_1.75rem] items-center gap-4";

export const ComparisonTable = ({ rows, currencySymbol, onRemove }: ComparisonTableProps) => {
  const pinnedCount = rows.filter((row) => !row.isBaseline).length;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-muted/40 text-left">
      <div
        className={cn(
          GRID,
          "border-b border-border bg-muted/50 px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground/70"
        )}
      >
        <span>Scenario</span>
        <span className="text-right">Total</span>
        <span className="text-right">Difference vs live</span>
        <span aria-hidden />
      </div>

      {rows.map((row) => {
        const created = row.createdAt ? formatBEDateToMediumDateString(row.createdAt) : "";
        const deltaColor =
          row.delta > 0
            ? "text-danger"
            : row.delta < 0
              ? "text-success"
              : "text-muted-foreground/70";

        return (
          <div
            key={row.id}
            className={cn(
              GRID,
              "border-b border-border px-4 py-3 last:border-b-0",
              row.isBaseline && "bg-muted/50"
            )}
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">{row.label}</span>
                {row.isBaseline ? <Pill tone="neutral">Baseline</Pill> : null}
                {row.isLowest ? <Pill tone="success">Lowest</Pill> : null}
                {row.isHighest ? <Pill tone="danger">Highest</Pill> : null}
              </div>
              {created ? (
                <span className="text-[11px] text-muted-foreground/70">{created}</span>
              ) : null}
            </div>

            <span className="text-right text-sm tabular-nums text-foreground/90">
              {formatMoney(currencySymbol, row.total)}
            </span>

            <div className="flex flex-col items-end leading-tight">
              {row.isBaseline ? (
                <span className="text-sm text-muted-foreground/50">—</span>
              ) : (
                <>
                  <span className={cn("text-sm tabular-nums", deltaColor)}>
                    {formatSignedMoney(currencySymbol, row.delta)}
                  </span>
                  <span className={cn("text-[11px] tabular-nums", deltaColor)}>
                    {formatSignedPercent(row.deltaPct)}
                  </span>
                </>
              )}
            </div>

            <div className="flex justify-end">
              {row.isBaseline || row.budgetId === undefined ? null : (
                <Button
                  type="button"
                  color="gray"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onRemove(row.budgetId as number)}
                  aria-label={`Remove ${row.label} from comparison`}
                >
                  <X aria-hidden />
                </Button>
              )}
            </div>
          </div>
        );
      })}

      {pinnedCount === 0 ? (
        <p className="px-4 py-3 text-left text-[13px] text-muted-foreground/70">
          Pin a saved version above to compare it against your current budget.
        </p>
      ) : null}
    </div>
  );
};

const PILL_TONES = {
  neutral: "bg-muted text-muted-foreground",
  success: "bg-success/15 text-success",
  danger: "bg-danger/15 text-danger",
} as const;

const Pill = ({ tone, children }: { tone: keyof typeof PILL_TONES; children: string }) => (
  <span
    className={cn(
      "shrink-0 rounded px-1.5 py-px text-[10px] font-medium uppercase tracking-wide",
      PILL_TONES[tone]
    )}
  >
    {children}
  </span>
);
